// EnableMFAModal.jsx 🔐 Enable MFA (TOTP)
import React, { useEffect, useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import "./styles/EnableMFAModal.css"; // 🆕 Style aligned with Profile.css

export default function EnableMFAModal({ show, onHide, onSuccess, setUser }) {
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (show) {
      fetchSecret();
    } else {
      resetState();
    }
  }, [show]);

  const fetchSecret = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/mfa_generate_secret.php", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data?.secret && data?.uri) {
        setSecret(data.secret);
        setUri(data.uri);
      } else {
        setError("Error generating MFA secret.");
      }
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!token) return;
    setVerifying(true);
    setError("");
    try {
      const res = await fetch("/api/mfa_verify_token.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
		if (typeof setUser === "function") {
			setUser((prev) => ({ ...prev, mfa_secret: "ENABLED" }));
		}
        setTimeout(() => {
          if (typeof onSuccess === "function") {
            onSuccess();
          }
          onHide();
        }, 1000);
      } else {
        setError(data.error || "Invalid MFA code.");
      }
    } catch {
      setError("Network error.");
    } finally {
      setVerifying(false);
    }
  };

  const resetState = () => {
    setSecret("");
    setUri("");
    setToken("");
    setLoading(false);
    setError("");
    setSuccess(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" dialogClassName="custom-mfa-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-shield-lock-fill me-2 text-warning" />
          Enable Multi-Factor Authentication
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" variant="warning" />
          </div>
        ) : (
          <>
            <p>
              Scan this QR code using <strong>Microsoft Authenticator</strong> or any TOTP-compatible app.
            </p>

            {uri && (
              <div className="text-center mb-3">
                <QRCodeCanvas
                  value={uri}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin
                />
                <p className="mt-3 small" style={{ color: "#f8f9fa" }}>
                  Manual key: <code>{secret}</code>
                </p>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>6-digit verification code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter TOTP code"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </Form.Group>

            {error && <div className="alert alert-danger py-2">{error}</div>}
            {success && <div className="alert alert-success py-2">MFA successfully enabled!</div>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn-profile" onClick={onHide}>
          Cancel
        </button>
        <button
          className="btn-profile btn-success"
          onClick={handleVerify}
          disabled={!token || verifying}
        >
          {verifying ? <Spinner size="sm" animation="border" /> : "Activate"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
